import type { ReactNode } from "react";

import { Box, Column, Row, Skeleton, SkeletonBox } from "@hightouch/htui";

type Props = {
  main: ReactNode;
  properties: ReactNode;
  sidebarLabel: string;
};

/** A task page's body: the work in the main column, with its details beside it. */
export const DetailLayout = ({ main, properties, sidebarLabel }: Props) => (
  <Box
    display="flex"
    flexDirection={{ base: "column", lg: "row" }}
    alignItems={{ lg: "flex-start" }}
    justifyContent="center"
    gap={{ base: 8, lg: 16 }}
    px={6}
    py={8}
  >
    <Column flex={1} minWidth={0} gap={8} maxWidth="3xl">
      {main}
    </Column>
    <Column
      as="aside"
      aria-label={sidebarLabel}
      width={{ base: "100%", lg: "xs" }}
      flexShrink={0}
      gap={8}
    >
      {properties}
    </Column>
  </Box>
);

const SKELETON_PROPERTIES = ["a", "b", "c", "d"];

/** The detail layout's bones, for a page whose data hasn't arrived. */
export const DetailLayoutSkeleton = () => (
  <Skeleton isLoading>
    <DetailLayout
      sidebarLabel="Loading"
      main={
        <>
          <SkeletonBox height={8} width="50%" borderRadius="md" />
          <SkeletonBox height={4} width="80%" borderRadius="sm" />
          <SkeletonBox height={24} borderRadius="lg" />
          <SkeletonBox height={24} borderRadius="lg" />
        </>
      }
      properties={
        <Column gap={3}>
          {SKELETON_PROPERTIES.map((key) => (
            <Row key={key} gap={2} align="center" height={8}>
              <SkeletonBox width={20} height={3} borderRadius="sm" />
              <SkeletonBox flex={1} height={6} borderRadius="md" />
            </Row>
          ))}
        </Column>
      }
    />
  </Skeleton>
);

/** Stands in for a section whose data is still on its way. */
export const DetailSectionSkeleton = () => (
  <Skeleton isLoading>
    <Column gap={3}>
      <SkeletonBox height={4} width="60%" borderRadius="sm" />
      <SkeletonBox height={4} width="40%" borderRadius="sm" />
    </Column>
  </Skeleton>
);
